import { useState, useRef, useEffect, useMemo, useContext } from 'react';
import {
  BoldEnum,
  GuestAddAddress as GuestAddress,
  Heading,
  HeadingEnum,
  Text,
  TextEnum,
  SideSheet,
} from '@desquared/wave-components-library';
import { AddressServicabillitySideSheet } from '@/store/AddressServicabillity';
import { useTranslations } from 'next-intl';
import { useConfiguration } from '@/contexts/ConfigContext';
import { useRouter } from 'next/router';
import { constructAddressLine } from '@/utils/products/products';
import { getShopsV3ByAddressLine } from '@/api/Shops';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import { isEmpty } from 'lodash';
import styles from './addressServicabillityAside.module.css';
import { Shop } from '@/store/shop';

export function AddressServicabillityAside({ responsiveLogo }) {
  const router = useRouter();
  const [sideSheet, setSideSheet] = useState(false);
  const [isValid, setIsvalid] = useState(false);
  const [address, setAddress] = useState();
  const [alertStatus, setAlertStatus] = useState({});
  const asideRef = useRef(null);
  const t = useTranslations();
  const p = useTranslations('common.asides.postal_check');
  const { config } = useConfiguration();
  const { selectedService } = useContext(DeliveryServicesContext);

  const addressRestrictions = useMemo(() => {
    if (!config) return;

    let restrictions = {};

    if (config?.fields?.address) {
      for (const key of ['street', 'streetNo', 'postalCode']) {
        restrictions[key] = {
          maxLength: config?.fields?.address?.[key]?.maxLength,
          minLength: config?.fields?.address?.[key]?.minLength,
          required: true,
          errorMessage: {
            required: t('common.guest_address.errors.required'),
            maxLength: t('common.guest_address.erros.maxLength', { number: config?.fields?.address?.[key]?.maxLength }),
            minLength: t('common.guest_address.errors.minLength', {
              number: config?.fields?.address?.[key]?.minLength,
            }),
          },
        };
      }

      if (config?.fields?.address?.postalCode?.format === 'ALPHANUMERIC') {
        restrictions.postalCode.pattern = /^[A-Za-z0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.alphanumeric'),
        };
      } else {
        restrictions.postalCode.pattern = /^[0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.numeric'),
        };
      }
    }

    if ('postalCode' in restrictions) {
      restrictions['postal'] = { ...restrictions.postalCode };
      delete restrictions['postalCode'];
    }

    return restrictions;
  }, [config, t]);

  useEffect(() => {
    const AddressServicabillitySideSheet$ = AddressServicabillitySideSheet.subscribe(setSideSheet);

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      AddressServicabillitySideSheet$.unsubscribe();
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = e => {
    if (asideRef.current && !asideRef.current.contains(e.target)) {
      AddressServicabillitySideSheet.close();
    }
  };

  function handleAddressUpdate(value, valid) {
    setIsvalid(valid);
    if (valid) setAddress({ ...value, postalCode: value.postal });
  }

  async function handleSubmit() {
    const addressLine = constructAddressLine(address);
    const shops = await getShopsV3ByAddressLine(selectedService?.id, addressLine, address?.postal);
    if (!shops || shops?.shops?.length === 0) {
      setAlertStatus({ status: 'error', message: p('error_text') });
    } else {
      setAlertStatus({ status: 'success', message: p('success', { serviceTitle: selectedService?.title }) });
      Shop.setShopByDeliveryId(selectedService?.id, shops);
      Shop.setSelectedShop(shops[0]);
      AddressServicabillitySideSheet.update({
        ...AddressServicabillitySideSheet.getValue(),
        savedAddress: address,
        savedAddressLine: addressLine,
      });
    }
  }

  if (!sideSheet) return null;
  return (
    <div ref={asideRef}>
      <SideSheet
        header={t('common.asides.address_servicabillity.header')}
        hasClose={true}
        onSubmit={handleSubmit}
        buttonText={t('common.asides.list_addition.save')}
        isDisabled={!isValid}
      >
        <GuestAddress
          restrictions={addressRestrictions}
          autocompleteConfig={{ restrictAutoCompleteToCountry: process.env.defaultAutocompleteCountryCode }}
          onValidAddressChange={handleAddressUpdate}
          map={{
            apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
            center: {
              lat: process.env.defaultMapCenter.lat,
              lon: process.env.defaultMapCenter.lng,
            },
            locale: router.locale,
            stigmas: [],
            logo: responsiveLogo,
          }}
          wordings={{
            footerBtn: t('common.guest_address.header'),
            formPlaceholders: {
              address: t('common.guest_address.form_fields.address'),
              street: t('common.guest_address.form_fields.street'),
              streetNo: t('common.guest_address.form_fields.street_no'),
              postal: t('common.guest_address.form_fields.postal'),
            },
          }}
        />
        {!isEmpty(alertStatus) && (
          <div className={styles.alertContainer}>
            <div className={alertStatus.status === 'error' ? styles.alertError : styles.alertSuccess}>
              {alertStatus.message}
            </div>
          </div>
        )}
      </SideSheet>
    </div>
  );
}
